import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';

const GOLD = "#E8CDA9";
const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Josefin+Sans:ital,wght@0,300;0,400;0,600;0,700&family=Outfit:wght@700;800&display=swap');`;

const PLANS = [
  {
    id: 'fyltra',
    name: 'Fyltra',
    price: '14.99',
    features: ['Dashboard & statistics', 'Trading journal', 'Up to 3 accounts', 'Trading plan', 'TradingView integrated', 'Cancel anytime'],
    highlight: false,
  },
  {
    id: 'fyltra_plus',
    name: 'Fyltra+',
    price: '24.99',
    badge: 'Popular',
    features: ['Everything in Fyltra', 'AI Coach (Groq LLaMA)', 'Pattern analysis', 'MT5 real-time sync', 'Up to 10 accounts', 'Flexible cancellation'],
    highlight: true,
  },
  {
    id: 'fyltra_pro',
    name: 'Fyltra Pro',
    price: '39.99',
    features: ['Everything in Fyltra+', 'Unlimited MT4/MT5', 'Interactive replay', 'Advanced TradingView', 'Unlimited accounts', 'Priority support'],
    highlight: false,
  },
];

export default function Plans() {
  const [user, setUser]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [paying, setPaying]     = useState(null);
  const [error, setError]       = useState('');

  useEffect(() => {
    // Handle PKCE code from Supabase email confirmation
    const code = new URLSearchParams(window.location.search).get('code');
    if (code) {
      supabase.auth.exchangeCodeForSession(code).then(({ data, error: err }) => {
        if (!err && data?.session?.user) {
          window.history.replaceState({}, '', '/plans');
          setUser(data.session.user);
        }
        setLoading(false);
      });
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        // Already has active plan → go to app
        const plan = localStorage.getItem('fyltra_plan');
        if (plan) { window.location.href = '/app'; return; }
        setUser(session.user);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      if (session?.user) {
        setUser(session.user);
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  const selectPlan = async (planId) => {
    if (!user) { window.location.href = '/register'; return; }
    if (!user.email_confirmed_at) return;
    setPaying(planId);
    setError('');
    try {
      const res = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planId, email: user.email, existingUser: true }),
      });
      const { url, error: err } = await res.json();
      if (err || !url) { setError('Unable to start checkout. Please try again.'); setPaying(null); return; }
      window.location.href = url;
    } catch {
      setError('Network error. Please try again.');
      setPaying(null);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#060608', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{FONTS}</style>
        <div style={{ width: 28, height: 28, border: '2px solid rgba(232,205,169,0.2)', borderTopColor: GOLD, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Not authenticated → redirect to register
  if (!user) {
    window.location.href = '/register';
    return null;
  }

  // Email not confirmed → show confirmation pending screen
  if (!user.email_confirmed_at) {
    return (
      <div style={{ minHeight: '100vh', background: '#060608', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <style>{FONTS}</style>
        <div style={{ maxWidth: 380, width: '100%', textAlign: 'center' }}>
          <a href="/"><img src="/fyltra-logo-white.svg" style={{ height: 34, marginBottom: 44 }} alt="Fyltra" /></a>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(232,205,169,0.1)', border: '1px solid rgba(232,205,169,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 24 }}>✉</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#f0ede8', fontFamily: "'Josefin Sans',sans-serif", marginBottom: 12 }}>Confirm your email first</div>
          <div style={{ fontSize: 13, color: 'rgba(245,242,234,0.4)', lineHeight: 1.75, fontFamily: "'Josefin Sans',sans-serif", fontWeight: 300 }}>
            We sent a link to <span style={{ color: GOLD }}>{user.email}</span>.<br/>Click it to continue to plan selection.
          </div>
          <div style={{ marginTop: 28 }}>
            <a href="/" style={{ fontSize: 12, color: 'rgba(232,205,169,0.35)', fontFamily: "'Josefin Sans',sans-serif", textDecoration: 'none' }}>← Back to home</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#060608', color: '#f0ede8', fontFamily: "'Outfit',sans-serif", padding: '48px 24px' }}>
      <style>{FONTS}</style>
      <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }`}</style>

      {/* glow */}
      <div style={{ position: 'fixed', top: '20%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 500, borderRadius: '50%', background: 'radial-gradient(ellipse,rgba(232,205,169,0.05) 0%,transparent 70%)', pointerEvents: 'none', filter: 'blur(60px)' }} />

      <div style={{ maxWidth: 960, margin: '0 auto', animation: 'fadeUp 0.4s both' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <a href="/"><img src="/fyltra-logo-white.svg" style={{ height: 32, opacity: 0.9 }} alt="Fyltra" /></a>
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ display: 'inline-block', background: 'rgba(232,205,169,0.07)', border: '1px solid rgba(232,205,169,0.2)', borderRadius: 20, padding: '4px 14px', fontSize: 11, color: GOLD, fontFamily: "'Josefin Sans',sans-serif", letterSpacing: '0.1em', marginBottom: 16 }}>
            EMAIL CONFIRMED
          </div>
          <h1 style={{ fontSize: 'clamp(24px,4vw,40px)', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 10, color: '#fff' }}>
            Choose your plan
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(245,242,234,0.4)', fontWeight: 300, fontFamily: "'Josefin Sans',sans-serif" }}>
            Signed in as <span style={{ color: GOLD }}>{user.email}</span>
          </p>
        </div>

        {error && (
          <div style={{ maxWidth: 400, margin: '0 auto 24px', padding: '12px 16px', background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.2)', borderRadius: 10, fontSize: 13, color: '#ff8888', textAlign: 'center', fontFamily: "'Josefin Sans',sans-serif" }}>
            {error}
          </div>
        )}

        {/* Plans grid */}
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'stretch' }}>
          {PLANS.map((plan) => (
            <div key={plan.id} style={{ flex: '1 1 260px', maxWidth: 300, background: plan.highlight ? 'rgba(232,205,169,0.06)' : 'rgba(255,255,255,0.03)', border: `1px solid ${plan.highlight ? 'rgba(232,205,169,0.35)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 20, padding: '28px 24px', position: 'relative', boxShadow: plan.highlight ? '0 0 40px rgba(232,205,169,0.08)' : 'none', display: 'flex', flexDirection: 'column' }}>
              {plan.badge && (
                <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg,#e8cda9,#c9aa82)', color: '#111', borderRadius: 20, padding: '3px 14px', fontSize: 10, fontWeight: 700, fontFamily: "'Josefin Sans',sans-serif", letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
                  {plan.badge}
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: plan.highlight ? GOLD : 'rgba(255,255,255,0.5)', fontFamily: "'Josefin Sans',sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>{plan.name}</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                  <span style={{ fontSize: 36, fontWeight: 800, color: '#fff', letterSpacing: '-0.04em', lineHeight: 1 }}>€{plan.price.split('.')[0]}</span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: 'rgba(255,255,255,0.5)', lineHeight: 1, paddingBottom: 3 }}>.{plan.price.split('.')[1]}</span>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', fontFamily: "'Josefin Sans',sans-serif", paddingBottom: 3, marginLeft: 2 }}>/mo</span>
                </div>
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', flex: 1 }}>
                {plan.features.map((f, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'rgba(245,242,234,0.55)', fontFamily: "'Josefin Sans',sans-serif", fontWeight: 300, marginBottom: 8, lineHeight: 1.4 }}>
                    <span style={{ color: plan.highlight ? GOLD : 'rgba(232,205,169,0.4)', fontSize: 10, flexShrink: 0 }}>◆</span>
                    {f}
                  </li>
                ))}
              </ul>

              <button onClick={() => selectPlan(plan.id)} disabled={!!paying} style={{ width: '100%', padding: '13px', background: plan.highlight ? 'linear-gradient(135deg,#e8cda9,#c9aa82)' : 'rgba(255,255,255,0.08)', border: plan.highlight ? 'none' : '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: plan.highlight ? '#111' : '#f0ede8', fontFamily: "'Josefin Sans',sans-serif", fontWeight: 700, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: paying ? 'not-allowed' : 'pointer', opacity: paying && paying !== plan.id ? 0.5 : 1, transition: 'all 0.2s', boxShadow: plan.highlight ? '0 4px 20px rgba(232,205,169,0.2)' : 'none' }}>
                {paying === plan.id ? '···' : 'Start now →'}
              </button>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 32, fontSize: 11, color: 'rgba(255,255,255,0.18)', fontFamily: "'Josefin Sans',sans-serif", lineHeight: 1.7 }}>
          Secure payment via Stripe · Cancel anytime · No hidden fees
        </div>
      </div>
    </div>
  );
}
